import {Component, computed, effect, ElementRef, inject, OnDestroy, viewChild} from '@angular/core';
import {TaListBase} from '../../../pages/ta-list-base.directive';
import {SubmissionSearchCriteria} from '../../../../api';
import {
  AdvancedSearchParameters,
  emptyAdvancedSearchParameters,
  SearchTaList,
  toTAStatusText
} from '../../../../util/app.types';
import {Button} from 'primeng/button';
import {Chip} from 'primeng/chip';
import {TableModule} from 'primeng/table';
import {DatePipe, NgClass} from '@angular/common';
import {ErrorMessageService} from '../../../../services/error-message.service';
import {ScrollHeightDirective} from '../../../directives/scroll-height.directive';
import {PrepaidIndicatorComponent} from '../../prepaid-indicator/prepaid-indicator.component';
import {AttachmentIndicatorComponent} from '../../attachment-indicator/attachment-indicator.component';
import {TaCommentCount} from '../../ta-comments/ta-comment-count';
import {toSubmissionSearchCriteria} from '../../../../datasources/ta-search-datasource';
import {SubmissionSearchBarComponent} from '../submission-search-bar/submission-search-bar.component';

@Component({
  selector: 'all-submissions-search',
  imports: [
    TaCommentCount,
    SubmissionSearchBarComponent,
    Button,
    Chip,
    TableModule,
    DatePipe,
    NgClass,
    ScrollHeightDirective,
    PrepaidIndicatorComponent,
    AttachmentIndicatorComponent,
  ],
  templateUrl: './all-submissions-search.component.html',
  styleUrl: './all-submissions-search.component.scss'
})
export class AllSubmissionsSearchComponent extends TaListBase implements OnDestroy {
  errorMessageService = inject(ErrorMessageService);
  private scrollContainer = viewChild<ElementRef>('scrollContainer');
  private loadMoreSentinel = viewChild<ElementRef>('loadMoreSentinel');
  private observer: IntersectionObserver | undefined;

  constructor() {
    super();
    effect(() => {
      const el = this.loadMoreSentinel()?.nativeElement;
      const root = this.scrollContainer()?.nativeElement;
      this.observer?.disconnect();
      if (el && root) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting) {
              this.loadMore();
            }
          },
          { root, threshold: 0.1 },
        );
        this.observer.observe(el);
      }
    });
  }

  advancedEnabled = false;
  basicSearchStr: string = '';
  advancedSearchParameters: AdvancedSearchParameters = emptyAdvancedSearchParameters();

  currentPage = 0;

  financeResults = computed<SearchTaList | undefined>(() => this.store.financeSearchState().results);
  hasSearched = computed<boolean>(() => this.store.financeSearchState().hasSearched);
  isLoading = computed(() => this.store.isLoadingData());
  canLoadMore = computed(() => {
    const results = this.financeResults();
    if (!results || !results.totalCount) return true;
    return (results.currentCount ?? 0) < results.totalCount;
  });

  searchGuidance = computed(() => {
    if (this.permissionService.hasGlobalSearch()) {
      return 'Use the search above to find TA submissions.';
    }
    return 'Use the search above to find TA submissions in the ' + (!!this.permissionService.currentUser()?.managedUnits ? this.permissionService.currentUser()!.managedUnits!.length > 1 ? 'units you manage.' : 'unit you manage.' : 'unit you manage.');
  });

  override ngOnInit(): void {
    super.ngOnInit();

    const savedState = this.store.financeSearchState();
    this.advancedEnabled = savedState.advancedEnabled;
    this.basicSearchStr = savedState.basicSearchStr;
    this.advancedSearchParameters = {...savedState.advancedSearchParameters};
    this.currentPage = savedState.currentPage;
  }

  runSearch() {
    this.currentPage = 0;
    this.store.updateFinanceSearchParams({
      advancedEnabled: this.advancedEnabled,
      basicSearchStr: this.basicSearchStr,
      advancedSearchParameters: {...this.advancedSearchParameters},
      currentPage: this.currentPage,
    });
    if (this.advancedEnabled) {
      this.runAdvancedSearch();
    } else {
      this.runBasicSearch();
    }
  }

  runBasicSearch(): void {
    if (this.basicSearchStr.trim() === '') {
      this.errorMessageService.showWarningMessage('Search requires keyword.')
    } else {
      this.taSearchDatasource.basicFinanceSearchSubmissions(this.basicSearchStr, this.currentPage);
    }
  }

  runAdvancedSearch(): void {
    this.taSearchDatasource.advancedFinanceSearchSubmissions(this.advancedSearchParameters, this.currentPage);
  }

  override ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  loadMore(): void {
    if (!this.canLoadMore() || this.isLoading()) return;
    this.currentPage++;
    this.store.updateFinanceSearchParams({currentPage: this.currentPage});
    if (this.advancedEnabled) {
      this.taSearchDatasource.loadMoreFinanceSearch(this.advancedSearchParameters, this.currentPage);
    } else {
      this.taSearchDatasource.loadMoreBasicFinanceSearch(this.basicSearchStr, this.currentPage);
    }
  }

  clear() {
    this.basicSearchStr = '';
    this.advancedSearchParameters = emptyAdvancedSearchParameters();
    this.store.updateFinanceSearchParams({
      basicSearchStr: '',
      advancedSearchParameters: {...this.advancedSearchParameters},
      currentPage: 0,
      results: undefined,
      hasSearched: false,
    });
    this.store.updateTaSubmission(undefined);
  }

  setMode(advancedEnabled: boolean) {
    this.advancedEnabled = advancedEnabled;
    this.store.updateFinanceSearchParams({advancedEnabled});
  }

  exportResults(): void {
    let criteria: SubmissionSearchCriteria;
    if (this.advancedEnabled) {
      criteria = toSubmissionSearchCriteria(this.advancedSearchParameters);
    } else {
      criteria = { searchTerm: this.basicSearchStr };
    }
    this.taSearchDatasource.exportSearchResults(criteria);
  }

  protected readonly toTAStatusText = toTAStatusText;
}
